// frontend/packages/business-console/src/components/layout/Sidebar.jsx
//
// Sidebar contents shared between the desktop persistent panel and the
// mobile temporary Drawer. Layout deliberately mirrors UpRyzze Tools'
// AppShell so the two surfaces feel like one product:
//   - Brand header at top
//   - Grouped nav with section captions and dividers between groups
//   - Active item rendered with brand-blue tint + bold label
//   - Tooltip on hover (right-anchored) showing the item description
//   - Footer with business name + signed-in user + logout
//
// Inbox unread count is polled here (every 30s while the page is visible)
// so a Badge on the Inbox row is always close to fresh.

import React, { useContext, useMemo } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Badge,
  Box,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';

import { BusinessContext } from '../../contexts/BusinessContext';
import { useAuth } from '../../contexts/AuthContext';
import { Workspace_Pages } from '../../pages/pageConfig';
import { NAV_GROUPS } from './nav-config';
import { upryzzeColorGradient } from '../../config/styles';
import InstallAppButton from '../../pwa/InstallAppButton';
import { useInboxUnread } from '../../contexts/InboxUnreadContext';
import useTenantFeatures from '../../hooks/useTenantFeatures';
import { hiddenInDistrictMode, isDistrictMode } from './districtMode';

export const SIDEBAR_WIDTH = 248;

/**
 * Resolve `Workspace_Pages` items by key. Items missing from pageConfig
 * are silently dropped (defensive — keeps a stale nav-config from crashing
 * the shell during a refactor), as are items whose `feature` is not on and
 * items school district mode hides (districtMode.js).
 */
function resolveItems(items, basePath, features) {
  const byKey = new Map(Workspace_Pages.map((p) => [p.key, p]));
  return items
    .filter((it) => !it.feature || features[it.feature] === true)
    .filter((it) => !hiddenInDistrictMode(it.key, features))
    .map((it) => {
      const page = byKey.get(it.key);
      if (!page) return null;
      const href = page.path
        ? `${basePath}/${page.path}`
        : basePath || '/login';
      const label = page.navLabel || page.key;
      return { ...it, href, label, page };
    })
    .filter(Boolean);
}

function isActive(pathname, basePath, page) {
  if (!basePath) return false;
  if (page.key === 'home') {
    return pathname === basePath || pathname === `${basePath}/home`;
  }
  // Use a slash boundary so /crm doesn't claim activeness for /crm-something
  // and /inbox doesn't claim it for /inbox/:id (we DO want /inbox/:id to mark
  // Inbox active, which `startsWith(prefix + "/")` does correctly).
  const prefix = `${basePath}/${page.path}`;
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export default function Sidebar({ onNavigate }) {
  const location = useLocation();
  const { business, loading: businessLoading } = useContext(BusinessContext);
  const { user } = useAuth();

  const basePath = useMemo(
    () => (business?.slug ? `/business/${business.slug}` : ''),
    [business?.slug]
  );

  const businessName = businessLoading
    ? 'Loading…'
    : business?.name || 'Console';
  const userName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
    user?.email ||
    '';

  // Inbox unread count — shared poll (also feeds the mobile bottom nav and
  // Home "Needs you" row) so there's one source, not three intervals.
  const unreadCount = useInboxUnread();

  // Opt-in pages (nav items with a `feature`) show only when that tenant
  // feature is on; hidden while the flags load so they never flash in.
  const { features } = useTenantFeatures();

  const renderIcon = (Icon, key) => {
    const node = <Icon fontSize="small" />;
    if (key === 'inbox' && unreadCount > 0) {
      return (
        <Badge
          color="error"
          badgeContent={unreadCount}
          max={99}
          overlap="circular"
        >
          {node}
        </Badge>
      );
    }
    return node;
  };

  return (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        color: 'text.primary',
        borderRight: 1,
        borderColor: 'divider',
      }}
    >
      {/* Brand header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          minHeight: 64,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 0.25,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography
            sx={{
              fontWeight: 800,
              letterSpacing: 0.2,
              fontSize: 18,
              backgroundImage: upryzzeColorGradient,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
            }}
          >
            UpRyzze
          </Typography>
          <Typography
            sx={{ color: 'text.secondary', fontSize: 13, fontWeight: 600 }}
          >
            Console
          </Typography>
        </Box>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontWeight: 600 }}
          noWrap
        >
          {businessName}
        </Typography>
      </Box>

      {/* Grouped nav */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {NAV_GROUPS.map((group, groupIdx) => {
          const items = resolveItems(group.items, basePath, features);
          if (!items.length) return null;
          return (
            <Box key={group.label} sx={{ mb: 0.5 }}>
              {groupIdx > 0 && <Divider sx={{ mx: 2, my: 1, opacity: 0.5 }} />}
              <Typography
                variant="caption"
                sx={{
                  px: 3,
                  pt: 1,
                  display: 'block',
                  textTransform: 'uppercase',
                  letterSpacing: 0.6,
                  fontWeight: 700,
                  color: 'text.secondary',
                  fontSize: 11,
                }}
              >
                {(isDistrictMode(features) && group.districtLabel) ||
                  group.label}
              </Typography>
              <List dense disablePadding>
                {items.map(
                  ({ key, icon: Icon, description, href, label, page }) => {
                    const active = isActive(location.pathname, basePath, page);
                    return (
                      <Tooltip
                        key={key}
                        title={description ?? ''}
                        placement="right"
                        enterDelay={400}
                        disableHoverListener={!description}
                      >
                        <ListItemButton
                          component={RouterLink}
                          to={href}
                          onClick={onNavigate}
                          selected={active}
                          sx={{
                            mx: 1.5,
                            my: 0.25,
                            borderRadius: 2,
                            color: 'text.primary',
                            '& .MuiListItemIcon-root': {
                              color: 'text.secondary',
                            },
                            '&.Mui-selected': {
                              backgroundColor: (t) =>
                                t.palette.mode === 'dark'
                                  ? 'rgba(37, 99, 235, 0.20)'
                                  : 'rgba(37, 99, 235, 0.10)',
                              color: 'primary.main',
                              '& .MuiListItemIcon-root': {
                                color: 'primary.main',
                              },
                              '&:hover': {
                                backgroundColor: (t) =>
                                  t.palette.mode === 'dark'
                                    ? 'rgba(37, 99, 235, 0.28)'
                                    : 'rgba(37, 99, 235, 0.16)',
                              },
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
                            {renderIcon(Icon, key)}
                          </ListItemIcon>
                          <ListItemText
                            primary={label}
                            primaryTypographyProps={{
                              fontSize: 14,
                              fontWeight: active ? 700 : 500,
                              color: 'inherit',
                            }}
                          />
                        </ListItemButton>
                      </Tooltip>
                    );
                  }
                )}
              </List>
            </Box>
          );
        })}
      </Box>

      {/* User + logout footer */}
      <Divider />
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block' }}
          >
            Signed in as
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: 'text.primary' }}
            noWrap
          >
            {userName || 'Not signed in'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <InstallAppButton size="small" />
          <Tooltip title="Log out">
            <IconButton
              component={RouterLink}
              to="/logout"
              size="small"
              aria-label="Log out"
              onClick={onNavigate}
              sx={{ color: 'text.secondary' }}
            >
              <LogoutOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}
